import styled from "@emotion/styled";
import { Skeleton, TablePagination } from "~/common/mui";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { C_OUTLINE_VARIANT, C_SURFACE } from "~/common/components/icon/Colors";
import SearchResultSkeleton from "../ListView/components/SearchResultSkeleton";
import { NewSRPTable } from "./NewTabularView";
import { calculateTotalTableWidth } from "./TanStackColumnDefs";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { useComponentSize } from "~/common/hooks/useComponentSize";

const TABLE_PINNING = { columnPinning: { left: ["name"] } };

interface NewTabularViewSkeletonProps {
  rowCount?: number;
  columnCount?: number;
}

interface SkeletonRowData {
  id: number;
}

const TECH_VALUE_COLUMN_WIDTH = 126;

export const NewTabularViewSkeleton = ({ rowCount = 8, columnCount = 12 }: NewTabularViewSkeletonProps) => {
  const { ref, width } = useComponentSize<HTMLDivElement>();
  const skeletonData = useMemo(() => Array.from({ length: rowCount }, (_, i) => ({ id: i })), [rowCount]);
  const isMinTablet = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();

  const columns: ColumnDef<SkeletonRowData>[] = useMemo(() => {
    const productColumn: ColumnDef<SkeletonRowData> = {
      id: "name",
      header: () => <Skeleton variant="rounded" width={247} height={26} animation="wave" />,
      size: 814,
      cell: () => <SkeletonProductCell />,
    };

    const techColumns: ColumnDef<SkeletonRowData>[] = Array.from({ length: columnCount }, (_, i) => ({
      id: `tech-${i}`,
      size: TECH_VALUE_COLUMN_WIDTH,
      header: () => <Skeleton variant="rounded" width={80} height={16} animation="wave" />,
      cell: () => <Skeleton variant="rounded" width={100} height={20} animation="wave" />,
    }));

    return [productColumn, ...techColumns];
  }, [columnCount]);

  const totalTableWidth = calculateTotalTableWidth(columns);
  const isTableLargerThanDesktop = totalTableWidth > width;

  let state = undefined;
  let fullWidth = true;
  if (!isMinTablet) {
    state = TABLE_PINNING;
    // fullWidth = false;
  } else if (!isDesktop || isTableLargerThanDesktop) {
    state = TABLE_PINNING;
    // fullWidth = false;
  } else {
    columns.slice(1, columns.length).forEach((c) => (c.size = 255));
    fullWidth = false;
  }

  return (
    <div ref={ref}>
      <NewSRPTable
        data={skeletonData}
        columns={columns}
        fullwidth={fullWidth}
        variant=""
        stickyHeader={true}
        state={state}
      />
      <StyledTablePagination
        component="div"
        count={rowCount * 20}
        page={0}
        onPageChange={() => {}}
        rowsPerPage={20}
        rowsPerPageOptions={[20]}
      />
    </div>
  );
};

const SkeletonProductCell = styled(SearchResultSkeleton)({
  margin: "-8px",
  border: "none",
  ["& .MuiCard-root"]: {
    border: "none",
  },
  ["& .MuiCardContent-root"]: {
    padding: "16px",
  },
});

const StyledTablePagination = styled(TablePagination)<{ component: React.ElementType }>({
  background: C_SURFACE,
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderTop: `none`,

  ["& .MuiToolbar-root > *"]: {
    visibility: "hidden",
  },
});
